import { State } from './state';
import { DB } from 'redux-database';
import { useState, useEffect, useRef, useCallback } from 'react';
import { dig, wait } from '../util/util';
import { useQueryAsState } from './history';
import { split, isEqual, compact } from 'lodash';
import { api, store } from './store';
import { Company, getRawCompany, searchCompanies } from './company';
import {
  Product,
  searchAdditionalProductByVariantId,
  getRawProduct,
  getRawProducts,
  getRawAdditionalProducts,
} from './product';
import { Category, getRawCategory, searchCategories, CategoryType } from './category';
import { Contract, getRawContract, getRawContracts } from './contract';
import { Shop, getRawShop, getRawShops } from './shop';
import { Event, getRawEvent, getRawEvents } from './event';
import { debounce } from 'lodash';
import { Order } from './order';
import { User, getRawUser, getRawUsers } from './user';
import { Venue, getRawVenue, getRawVenues } from './venue';
import { handleError } from './notification';
import { PaginatedResponse } from './api';
import { Signal } from './signal';

/* INTERFACES */
export type OptionType =
  | 'company'
  | 'user'
  | 'product'
  | 'event'
  | 'shop'
  | 'category'
  | 'venue'
  | 'venue_with_single_general_admission'
  | 'contract'
  | 'additional-product'
  | 'shipping-product';

export interface GetParams {
  page: number;
  perPage: number;
  term?: string;
  type?: string;
}

export interface Tag {
  exclude: boolean;
  name: string;
}

export type SearchResultType = 'events' | 'merchProducts' | 'companies' | 'orders';

export type SearchResult = Event | Product | Company | Order;

export async function globalSearch(term: string): Promise<SearchResult[]> {
  const response = await api.search({ term });
  return response.data as SearchResult[];
}

export interface Paginator {
  page: number;
  setPage: (page: number) => void;
  perPage: number;
  setPerPage: (page: number) => void;
  totalCount: number;
  currentIds: string[];
  loading: boolean;
  loadingMore?: boolean;
  error?: Error;
  alternateLink?: string;
  reloadPage: () => void;
  permissions: Set<string>;
}

export interface PaginatedLoaderResponse {
  ids: string[];
  totalCount: number;
  alternateLink?: string;
  permissions: Set<string>;
}

export function paginatedLoaderResponseFrom(response: {
  data: Array<{ id: string }>;
  totalCount: number;
  alternateLink?: string;
  permissions?: Set<string>;
}): PaginatedLoaderResponse {
  return {
    ids: response.data.map((e) => e.id),
    totalCount: response.totalCount,
    alternateLink: response.alternateLink,
    permissions: response.permissions || new Set<string>(),
  };
}

type PaginatedLoader = (page: number, perPage: number) => Promise<PaginatedLoaderResponse>;

export type Collection =
  | Company
  | Event
  | Product
  | Shop
  | User
  | Venue
  | Category
  | Contract
  | { id: string; name?: string };

/* METHODS */
export async function searchCollection({
  term,
  types,
  additionalParams,
  perPage = 10,
}: {
  term: string;
  types: OptionType[];
  // Flexible way of setting additional params, this can be any
  additionalParams?: JSONObject;
  perPage?: number;
}): Promise<{
  results: Collection[] & { id?: string };
  total: number;
}> {
  const terms = split(term.trim(), ' ').filter(Boolean);
  const params = {
    page: 0,
    perPage,
    term: `*${terms.join('*&*')}*`,
    ...additionalParams,
  };

  const apiPromises: Array<Promise<PaginatedResponse<Collection[]>>> = [];

  types.forEach((type) => {
    switch (type) {
      case 'company':
        apiPromises.push(
          searchCompanies({ terms: terms.map((e) => `*${e}*`) }).then((companies) => ({
            data: companies,
            page: 1,
            perPage,
            totalPages: 1,
            totalCount: companies.length,
            permissions: new Set(),
          }))
        );
        break;
      case 'event':
        apiPromises.push(getRawEvents(params));
        break;
      case 'product':
        apiPromises.push(getRawProducts(params));
        break;
      case 'additional-product':
        apiPromises.push(getRawAdditionalProducts(params));
        break;
      case 'shipping-product':
        apiPromises.push(
          getRawAdditionalProducts({
            ...params,
            type: 'shipping',
          })
        );
        break;
      case 'shop':
        apiPromises.push(getRawShops(params));
        break;
      case 'user':
        apiPromises.push(getRawUsers(params));
        break;
      case 'venue':
        apiPromises.push(getRawVenues(params));
        break;
      case 'venue_with_single_general_admission':
        apiPromises.push(getRawVenues(params, { singleGeneralAdmission: true }));
        break;
      case 'category':
        apiPromises.push(searchCategories({ types: additionalParams?.categoryTypes as CategoryType[], ...params }));
        break;
      case 'contract':
        apiPromises.push(getRawContracts(params));
        break;
      default:
      // do nothing
    }
  });

  const results = await Promise.all(apiPromises);
  const items: Collection[] = [];
  let total = 0;

  results.forEach((result, index) => {
    if (result) {
      // Using any here because the type cannot be resolved
      // eslint-disable-next-line
      result.data.forEach((resultItem: any) => {
        if (resultItem.type === 'shipping') {
          resultItem.productVariants.forEach((productVariant: { id: string; description?: string; sku?: string }) => {
            items.push({
              ...resultItem,
              name: [resultItem.name, productVariant.sku, productVariant.description].filter(Boolean).join(' - '),
              sku: productVariant.sku,
              id: productVariant.id,
              productId: resultItem.id,
              itemType: types[index],
            });
          });
        } else {
          items.push({
            ...resultItem,
            itemType: types[index],
          });
        }
      });
    }
    total += result?.totalCount;
  });

  return {
    results: items,
    total,
  };
}

export async function searchElementById(id: string, types: OptionType[]): Promise<Collection | undefined> {
  const possibleResults: Array<Promise<PaginatedResponse<Collection>>> = [];
  types.forEach((type) => {
    switch (type) {
      case 'company':
        possibleResults.push(getRawCompany(id));
        break;
      case 'event':
        possibleResults.push(getRawEvent(id));
        break;
      case 'product':
        possibleResults.push(getRawProduct(id));
        break;
      case 'shipping-product':
        possibleResults.push(searchAdditionalProductByVariantId(id));
        break;
      case 'shop':
        possibleResults.push(getRawShop(id));
        break;
      case 'user':
        possibleResults.push(getRawUser(id));
        break;
      case 'venue':
      case 'venue_with_single_general_admission':
        possibleResults.push(getRawVenue(id));
        break;
      case 'category':
        possibleResults.push(getRawCategory(id));
        break;
      case 'contract':
        possibleResults.push(getRawContract(id));
        break;
      default:
      // do nothing
    }
  });

  const results = await Promise.all(
    possibleResults.map((p, index) =>
      p.then((val) =>
        Promise.resolve({
          ...val.data,
          type: types[index],
          itemType: types[index],
        })
      )
    )
  );

  return results[0];
}

export async function searchElementsByIds(ids: string[], types: OptionType[]): Promise<Collection[] | undefined> {
  const possibleResults: Array<Promise<Collection | undefined>> = [];

  ids.forEach((id) => {
    possibleResults.push(searchElementById(id, types));
  });

  const results = compact(await Promise.all(possibleResults));

  return results.length > 0 ? results : undefined;
}

export function useForceUpdate(): () => void {
  const [, updateState] = useState(true);
  const forceUpdate = useCallback(() => {
    updateState((state) => !state);
  }, []);
  return forceUpdate;
}

export function useSignal(signal: Signal): void {
  const reload = useForceUpdate();
  useEffect(() => {
    signal.subscribe(() => {
      reload();
    });
    return () => signal.unsubscribe(reload);
  }, [signal]);
}

/* HOOKS */
export function useData<T>(query: (db: DB<State>) => T): T {
  const db = new DB(store.getState()!);
  const forceUpdate = useForceUpdate();
  const stateRef = useRef(query(db));
  const queryRef = useRef(query);
  queryRef.current = query;
  function updateData(): void {
    const db = new DB(store.getState()!);
    const queryResult = queryRef.current(db);
    const hasChanged = !isEqual(stateRef.current, queryResult);
    if (hasChanged) {
      stateRef.current = queryResult;
      forceUpdate();
    }
  }

  useEffect(() => {
    updateData();
  }, [query]);

  useEffect(() => {
    const unsubscribe = store.subscribe(updateData);
    return unsubscribe;
  }, []);
  return stateRef.current;
}

export function useAsync(
  load: () => Promise<unknown>,
  cacheKey: string[] = [],
  { handleError: shouldHandleError }: { handleError: boolean } = { handleError: false }
): [boolean, Error | undefined] {
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  async function handleLoad(): Promise<void> {
    setLoading(true);
    try {
      await load();
    } catch (e) {
      setError(e as Error);
      if (shouldHandleError) {
        handleError(e);
      }
    }
    setLoading(false);
  }

  useEffect(() => {
    handleLoad();
  }, cacheKey);
  return [loading, error];
}

export function useTask(options: { handleError?: boolean } = {}): {
  loading: boolean;
  error?: Error;
  run: (task: Promise<unknown>) => void;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  return {
    loading,
    error,
    run: async (task) => {
      setLoading(true);
      setError(undefined);
      try {
        await task;
      } catch (error) {
        setError(error as Error);
        if (options.handleError) {
          handleError(error);
        }
      } finally {
        setLoading(false);
      }
    },
  };
}

export function useAsyncValue<T>(
  load: () => Promise<T | undefined>,
  cacheKey: string[] = [],
  { setLoadingOnReload = false }: { setLoadingOnReload?: boolean } = {}
): [T | undefined, boolean, Error | undefined] {
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState<T>();
  async function handleLoad(): Promise<void> {
    try {
      const response = await load();
      setValue(response);
    } catch (e) {
      setError(e as Error);
    }
    setLoading(false);
  }

  useEffect(() => {
    if (setLoadingOnReload) {
      setLoading(true);
    }
    handleLoad();
  }, cacheKey);

  return [value, loading, error];
}

export function useAsyncValueWithSetter<T>(
  load: () => Promise<T | undefined>,
  cacheKey: string[] = [],
  { setLoadingOnReload = false }: { setLoadingOnReload?: boolean } = {}
): [T | undefined, boolean, Error | undefined, (value: T) => void] {
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState<T>();
  async function handleLoad(): Promise<void> {
    try {
      const response = await load();
      setValue(response);
    } catch (e) {
      setError(e as Error);
    }
    setLoading(false);
  }

  useEffect(() => {
    if (setLoadingOnReload) {
      setLoading(true);
    }
    handleLoad();
  }, cacheKey);

  return [value, loading, error, setValue];
}

export function useRemoteData<T>(
  load: () => Promise<unknown>,
  query: (db: DB<State>) => T,
  cacheKey: string[] = []
): [T, boolean, Error | undefined] {
  const data = useData(query);
  const [loading, error] = useAsync(load, cacheKey);

  return [data, loading, error];
}

export function usePolling(
  load: () => void,
  interval: number,
  { start }: { start: boolean } = { start: false }
): [boolean, () => void] {
  const [running, setRunning] = useState(start);

  const timerRef = useRef<number | undefined>();

  useEffect(() => {
    if (running && !timerRef.current) {
      load();
      timerRef.current = window.setInterval(load, interval); //for the type
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = undefined;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = undefined;
      }
    };
  }, [running]);

  return [running, () => setRunning((status) => !status)];
}

export interface SequentialPollingState {
  running: boolean;
  setRunning: (_: boolean) => void;
  toggle: () => void;
  start: () => void;
}

export function useSequentialPolling<T>({
  load,
  interval,
  start = false,
  onUpdate = () => true,
}: {
  load: () => Promise<T>;
  interval: number;
  start?: boolean;
  onUpdate?: (_: T) => boolean;
}): SequentialPollingState {
  const [running, setRunning] = useState(start);
  const runningRef = useRef(running);
  runningRef.current = running;

  useEffect(() => {
    (async () => {
      while (runningRef.current) {
        const result = await load();
        if (onUpdate(result)) {
          await wait(interval);
        } else {
          setRunning(false);
        }
      }
    })();
  }, [running]);

  // Make sure the polling stops on unmounting.
  useEffect(() => () => setRunning(false), []);

  return {
    running,
    toggle: () => setRunning((status) => !status),
    start: () => setRunning(true),
    setRunning,
  };
}

export function useRemoteSearch<T>(
  query: string,
  loadFunction: (term: string, additionalParams: JSONObject) => Promise<T>,
  minLength = 3,
  additionalParams = {}
): [T | undefined, boolean, Error | undefined] {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [results, setResults] = useState<T>();

  async function search(term: string, additionalParams: JSONObject): Promise<void> {
    if (term.length < minLength) {
      setResults(undefined);
      return;
    }

    setLoading(true);
    try {
      const response = await loadFunction(term, additionalParams);
      setResults(response);
    } catch (e) {
      setError(e as Error);
    }
    setLoading(false);
  }

  const debouncedSearch = useRef(
    debounce((term: string, additionalParams: JSONObject) => search(term, additionalParams), 300)
  ).current;

  useEffect(() => {
    debouncedSearch(query, additionalParams);
  }, [query]);

  return [results, loading, error];
}

export function useAppendedPaginatedResponse(loader: PaginatedLoader, cacheKey: string): Paginator {
  const [error, setError] = useState<Error | undefined>(undefined);
  const [currentQueryState, setParams] = useQueryAsState();

  const page = currentQueryState.page || 0;
  const perPage = currentQueryState.perPage || 10;

  const [totalCount, setTotalCount] = useState<{ [key: string]: number }>({});
  const [permissions, setPermissions] = useState<Set<string>>(new Set<string>());
  const [alternateLink, setAlternateLink] = useState<string | undefined>(undefined);
  const [loadingAll, setLoadingAll] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [idCache, setIdCache] = useState<{
    [key: string]: string[];
  }>({});

  const [iterator, setIterator] = useState(0);
  const idCacheKey = `${cacheKey}-${perPage}`;
  const refCacheKey = useRef('');
  refCacheKey.current = idCacheKey;

  useEffect(() => {
    if (!idCache[idCacheKey]) {
      setLoadingAll(true);
    } else if (page * perPage >= idCache[idCacheKey].length) {
      setLoadingMore(true);
    }

    loader(page, perPage)
      .then((args) => {
        if (idCacheKey !== refCacheKey.current) {
          return;
        }
        setLoadingAll(false);
        setLoadingMore(false);
        setTotalCount((prev) => ({ ...prev, [cacheKey]: args.totalCount }));
        setPermissions(args.permissions);
        setAlternateLink(args.alternateLink);

        setIdCache((prevCache) => {
          const existing = prevCache[idCacheKey] || [];
          const newIds = [...existing, ...args.ids.filter((id) => !existing.includes(id))];
          return {
            ...prevCache,
            [idCacheKey]: newIds,
          };
        });
      })
      .catch((e) => {
        setError(e);
      });
  }, [idCacheKey, page, perPage, iterator]);

  const currentIds = (idCache[idCacheKey] || []).slice(0, (page + 1) * perPage);

  return {
    totalCount: totalCount[cacheKey] || 0,
    alternateLink,
    currentIds,
    error,
    loading: loadingAll,
    loadingMore,
    page,
    permissions,
    perPage,
    reloadPage: (): void => {
      setIterator((val) => val + 1);
    },
    setPage: (newPage: number): void => {
      setParams({ page: newPage });
    },
    setPerPage: (newPerPage: number): void => {
      setParams({ perPage: newPerPage });
    },
  };
}

export function usePaginatedResponse(loader: PaginatedLoader, cacheKey: string, queryParamsPrefix?: string): Paginator {
  const pageQueryParamName = queryParamsPrefix ? `${queryParamsPrefix}Page` : 'page';
  const perPageQueryParamName = queryParamsPrefix ? `${queryParamsPrefix}PerPage` : 'perPage';
  const [error, setError] = useState<Error | undefined>(undefined);
  const [currentQueryState, setParams] = useQueryAsState();
  const page = parseInt(currentQueryState[pageQueryParamName]) || 0;
  const perPage = parseInt(currentQueryState[perPageQueryParamName]) || 10;
  const [totalCount, setTotalCount] = useState<{ [key: string]: number }>({});
  const [permissions, setPermissions] = useState<Set<string>>(new Set<string>());
  const [alternateLink, setAlternateLink] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [idCache, setIdCache] = useState<{
    [key: string]: { [page: string]: string[] };
  }>({});
  const [iterator, setIterator] = useState(0); // triggers a reload of content on changes
  const idCacheKey = `${cacheKey}-${perPage}`;
  const refCacheKey = useRef('');
  refCacheKey.current = idCacheKey;
  useEffect(() => {
    if (!dig(idCache, idCacheKey, page.toString())) {
      setLoading(true); // only show loading indicator if data is not available
    }

    loader(page, perPage)
      .then((args) => {
        if (idCacheKey !== refCacheKey.current) {
          return;
        }
        setLoading(false);
        setTotalCount({ ...totalCount, [cacheKey]: args.totalCount });
        setPermissions(args.permissions);
        setIdCache({
          ...idCache,
          [idCacheKey]: {
            ...idCache[idCacheKey],
            [page]: args.ids,
          },
        });
        setAlternateLink(args.alternateLink);
      })
      .catch((e) => {
        setError(e);
      });
  }, [idCacheKey, page, perPage, iterator]);
  function reloadPage(): void {
    setIterator((val) => val + 1);
  }
  const currentIds = (idCache[idCacheKey] || {})[page] || [];

  const setPage = (newPage: number): void => {
    setParams({ [pageQueryParamName]: newPage });
  };
  const setPerPage = (newPerPage: number): void => {
    setParams({ [perPageQueryParamName]: newPerPage });
  };
  return {
    page,
    setPage,
    perPage,
    setPerPage,
    totalCount: totalCount[cacheKey] || 0,
    currentIds,
    loading,
    error,
    reloadPage,
    alternateLink,
    permissions,
  };
}

export function useSubmit(
  load: () => Promise<void>,
  handleError?: (error: Error) => void,
  onSubmit?: () => void
): [() => void, boolean?, Error?] {
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  async function submit(): Promise<void> {
    setLoading(true);
    try {
      await load();
      if (onSubmit) {
        onSubmit();
      }
    } catch (e) {
      if (handleError) {
        handleError(e as Error);
      }
      setError(e as Error);
    }
    setLoading(false);
  }

  return [submit, loading, error];
}
